/**
 * Who lives on which lot in a multiplayer park, worked out without a server. Every
 * player claims a lot; if somebody who outranks you claims yours, you move to the first
 * free lot. Everyone runs the same rules on the same claims, so they agree.
 */
export interface LotClaim {
  id: string;
  lot: number;
  /** Opened this park. */
  host: boolean;
  /** When they joined (ms since epoch). */
  since: number;
}

/** The park's host comes first, then whoever got there first (ties broken by id). */
export function outranks(a: LotClaim, b: LotClaim): boolean {
  if (a.host !== b.host) return a.host;
  if (a.since !== b.since) return a.since < b.since;
  return a.id < b.id;
}

/**
 * The lot `me` should be on. You stay put unless someone who outranks you claims your
 * lot; then you take the first lot (in `preference` order) nobody has claimed.
 */
export function resolveLot(me: LotClaim, others: readonly LotClaim[], preference: readonly number[]): number {
  const contested = others.some((o) => o.lot === me.lot && outranks(o, me));
  if (!contested) return me.lot;
  for (const lot of preference) {
    if (lot !== me.lot && !others.some((o) => o.lot === lot)) return lot;
  }
  return me.lot;
}

/** Whose home to show on each lot, when claims overlap for a moment. */
export function lotOwners(claims: readonly LotClaim[]): Map<number, LotClaim> {
  const owners = new Map<number, LotClaim>();
  for (const c of claims) {
    const current = owners.get(c.lot);
    if (!current || outranks(c, current)) owners.set(c.lot, c);
  }
  return owners;
}
